import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { AuthShell } from '@/components/branding/auth-shell';

export const Route = createFileRoute('/signup')({
  component: SignupPage,
});

type Phase = 'idle' | 'submitting' | 'sent';

interface FormState {
  name: string;
  email: string;
  company: string;
  reason: string;
}

const EMPTY: FormState = { name: '', email: '', company: '', reason: '' };

function SignupPage(): JSX.Element {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [phase, setPhase] = useState<Phase>('idle');
  const [error, setError] = useState<string | null>(null);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]): void => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    if (!form.name.trim() || !form.email.trim() || !form.reason.trim()) {
      setError('Name, email, and reason are required.');
      return;
    }
    setPhase('submitting');
    try {
      const res = await fetch('/api/auth/request-access', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          company: form.company.trim() || null,
          reason: form.reason.trim(),
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `Request failed (${res.status})`);
      }
      setPhase('sent');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
      setPhase('idle');
    }
  };

  return (
    <AuthShell>
      <Card className="w-full max-w-md bg-surface/95 border-border/[0.16] shadow-2xl backdrop-blur-md">
        <CardHeader>
          <CardTitle className="font-display text-2xl">
            {phase === 'sent' ? "We'll be in touch" : 'Request access'}
          </CardTitle>
          <CardDescription>
            {phase === 'sent'
              ? "Thanks — we read every request and reply within 24h."
              : "SoftSync is invite-only right now. Tell us about you and we'll get back."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {phase === 'sent' ? (
            <div className="space-y-4">
              <p className="text-sm text-text-dim">
                Once we approve your request, you'll get an invite email with a link to set your password.
              </p>
              <a
                href="/login"
                className="inline-flex w-full items-center justify-center rounded border border-border/[0.16] bg-transparent py-2 text-sm text-text-dim transition-colors hover:border-accent hover:text-text"
              >
                Back to sign in
              </a>
            </div>
          ) : (
            <form
              onSubmit={(e) => {
                void onSubmit(e);
              }}
              className="space-y-3"
            >
              <div>
                <Label htmlFor="name">Your name</Label>
                <Input
                  id="name"
                  required
                  value={form.name}
                  onChange={(e) => update('name', e.target.value)}
                  disabled={phase === 'submitting'}
                />
              </div>
              <div>
                <Label htmlFor="email">Work email</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  autoComplete="email"
                  value={form.email}
                  onChange={(e) => update('email', e.target.value)}
                  disabled={phase === 'submitting'}
                />
              </div>
              <div>
                <Label htmlFor="company">Company (optional)</Label>
                <Input
                  id="company"
                  value={form.company}
                  onChange={(e) => update('company', e.target.value)}
                  disabled={phase === 'submitting'}
                />
              </div>
              <div>
                <Label htmlFor="reason">What do you want to use SoftSync for?</Label>
                <textarea
                  id="reason"
                  required
                  rows={3}
                  maxLength={500}
                  value={form.reason}
                  onChange={(e) => update('reason', e.target.value.slice(0, 500))}
                  disabled={phase === 'submitting'}
                  className="mt-1 w-full rounded border border-border/20 bg-surface-2 p-2 text-sm text-text placeholder:text-text-mute resize-none disabled:opacity-60"
                  placeholder="e.g. We're a 12-person trades agency in Manchester looking to consolidate inbox + dialer + pipeline…"
                />
                <p className="mt-1 text-[10px] text-text-mute font-mono text-right">
                  {form.reason.length}/500
                </p>
              </div>
              {error ? <p className="text-danger text-sm">{error}</p> : null}
              <Button type="submit" disabled={phase === 'submitting'} className="w-full">
                {phase === 'submitting' ? 'Sending…' : 'Request access'}
              </Button>
              <div className="text-center text-sm">
                <a
                  href="/login"
                  className="text-text-dim hover:text-accent transition-colors"
                >
                  Already have an account? Sign in
                </a>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </AuthShell>
  );
}
